import ContextPill from '../ui/ContextPill'
import LayeringPicker from '../layering/LayeringPicker'
import CollectionLayerRows from '../layering/CollectionLayerRows'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Modal, Spinner } from '../ui'
import { useCollection } from '../../hooks/useCollection'
import { useWearLog } from '../../hooks/useWearLog'
import {
  buildWearLogEntry,
  QUICK_WEAR_OCCASIONS,
  timeBucket,
  todayISO,
  wearDate,
  yesterdayISO,
} from '../../lib/wearLogContext'
import { localDateTimeValue } from '../../lib/localDay'
import {
  useFragranceSprayOverride,
  useSignificantDates,
  useUserProfileSettings,
} from '../../hooks/useUserDefaults'
import { normalizeSprayCount, resolveSprayPrefill, significantDateContext } from '../../lib/userDefaults'
import {
  abandonWearFunnelSession,
  emitWearFunnelEvent,
} from '../../lib/wearFunnelTelemetry'
import {
  useWearConfirmationVisibility,
  useWearFunnelLifecycle,
} from '../../hooks/useWearFunnelTelemetry'
import ProjectionCheckin from '../layering/ProjectionCheckin'
import { serializeLayerRows, resolveLayerLabels, oilCount } from '../../lib/layeringPicker'
import { projectionCheckin } from '../../lib/projectionCheckin'

const TIME_BUCKETS = ['morning', 'afternoon', 'evening', 'night']
const EVENING_CONTEXTS = ['gym', 'dinner', 'date', 'chill', 'other']

// Wear Model v2: one SOTD per day (DB-enforced); other labels unlimited.
const WEAR_LABEL_OPTIONS = [
  { value: 'sotd', label: 'SOTD' },
  { value: 'gym', label: 'Gym' },
  { value: 'work', label: 'Work' },
  { value: 'evening', label: 'Evening' },
  { value: 'night', label: 'Night' },
  { value: 'event', label: 'Event' },
  { value: 'other', label: 'Other' },
]

function defaultLabelForMode(mode) {
  if (mode === 'gym') return 'gym'
  if (mode === 'another') return 'other'
  return 'sotd'
}

function replaceDate(value, date) {
  const time = value?.split('T')[1] || localDateTimeValue().split('T')[1]
  return `${date}T${time}`
}

export default function WearQuickCapture({
  open,
  fragrance,
  onClose,
  onSaved,
  mode = 'sotd',
  initialDate = null,
  initialLayeringOpen = false,
  initialLayerLabels = [],
  telemetrySession = null,
}) {
  const { fragrances } = useCollection({ showSpecialty: true })
  const { addLog, updateLog, logs, loading: logsLoading, pendingCreate, pendingCreateError, retryPendingCreate } = useWearLog({ limit: 200 })
  const { profile, loading: profileLoading } = useUserProfileSettings()
  const {
    sprayOverride,
    loading: sprayOverrideLoading,
    saving: sprayOverrideSaving,
    error: sprayOverrideError,
    saveSprayOverride,
  } = useFragranceSprayOverride(fragrance?.id)
  const [date, setDate] = useState(todayISO())
  const [appliedAt, setAppliedAt] = useState(localDateTimeValue())
  const [appliedAtEditing, setAppliedAtEditing] = useState(false)
  const [selectedTimeBucket, setSelectedTimeBucket] = useState(timeBucket())
  const [eveningContext, setEveningContext] = useState('')
  const [occasion, setOccasion] = useState('')
  const [note, setNote] = useState('')
  const [sprays, setSprays] = useState('')
  const [selectedLayers, setSelectedLayers] = useState([])
  const [layeringOpen, setLayeringOpen] = useState(false)
  const [projectionValue, setProjectionValue] = useState(null)
  const [expanded, setExpanded] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [errorField, setErrorField] = useState(null)
  const [wearLabel, setWearLabel] = useState(defaultLabelForMode(mode))
  const [labelPickerOpen, setLabelPickerOpen] = useState(mode === 'another')
  // Duplicate guard: silent success is indistinguishable from failure, so users
  // retry and log the same wear twice (founder + beta user both did — tickets
  // ef459461 / afbb378f). Surface the existing entry BEFORE writing a second one.
  const [duplicateAcknowledged, setDuplicateAcknowledged] = useState(false)
  const [layerTargetId, setLayerTargetId] = useState('')
  const layerSaveLock = useRef(false)
  const [selectedSignificantDateId, setSelectedSignificantDateId] = useState(null)
  const sprayTouched = useRef(false)
  const confirmationRef = useRef(null)
  const saveErrorRef = useRef(null)
  const sprayDefault = useMemo(() => resolveSprayPrefill({
    fragranceOverride: sprayOverride,
    userDefault: profile.default_spray_count,
  }), [sprayOverride, profile.default_spray_count])
  const { dates: significantDates, error: significantDatesError } = useSignificantDates({ date })
  const selectedSignificantDate = significantDateContext(significantDates, selectedSignificantDateId)
  const sprayDefaultsLoading = profileLoading || sprayOverrideLoading
  const parsedSprays = normalizeSprayCount(sprays)
  const showSprayOverridePrompt = !sprayDefaultsLoading
    && sprayTouched.current
    && parsedSprays != null
    && parsedSprays !== normalizeSprayCount(sprayDefault)

  useEffect(() => {
    if (!open) return
    const now = new Date()
    setDate(initialDate || todayISO(now))
    setAppliedAt(initialDate ? replaceDate(localDateTimeValue(now), initialDate) : localDateTimeValue(now))
    setAppliedAtEditing(false)
    setSelectedTimeBucket(timeBucket(now))
    setEveningContext(mode === 'gym' ? 'gym' : '')
    setOccasion(mode === 'gym' ? 'Gym' : '')
    setNote('')
    sprayTouched.current = false
    setSprays(sprayDefault === '' ? '' : String(sprayDefault))
    setSelectedLayers(resolveLayerLabels(initialLayerLabels, fragrances))
    setLayeringOpen(initialLayeringOpen || initialLayerLabels.length > 0)
    setProjectionValue(null)
    setExpanded(true)
    setError('')
    setErrorField(null)
    setWearLabel(defaultLabelForMode(mode))
    setLabelPickerOpen(mode === 'another')
    setDuplicateAcknowledged(false)
    setLayerTargetId('')
    setSelectedSignificantDateId(null)
  }, [open, fragrance?.id, mode, initialLayeringOpen, initialDate, initialLayerLabels.join('|'), fragrances])

  useEffect(() => {
    if (open && !sprayTouched.current) setSprays(sprayDefault === '' ? '' : String(sprayDefault))
  }, [open, sprayDefault])

  useEffect(() => {
    setSelectedSignificantDateId(null)
    setLayerTargetId('')
    setDuplicateAcknowledged(false)
  }, [date, fragrance?.id])

  useEffect(() => {
    if (!error || errorField) return
    saveErrorRef.current?.scrollIntoView({ block: 'nearest' })
  }, [error, errorField])

  useWearFunnelLifecycle(telemetrySession)
  useWearConfirmationVisibility(
    telemetrySession,
    confirmationRef,
    fragrance?.id,
    open && Boolean(fragrance),
  )

  // Wears of THIS bottle already recorded on the chosen day.
  const sameDayWears = useMemo(() => {
    if (!fragrance?.id) return []
    return logs.filter(log => wearDate(log) === date && log.fragrance_id === fragrance.id)
  }, [logs, date, fragrance?.id])

  if (!fragrance) return null

  // "Sep 5, 6:54 AM" — the compact applied-at row reads local wall-clock time.
  const appliedAtLabel = (() => {
    const parsed = new Date(appliedAt)
    if (Number.isNaN(parsed.getTime())) return 'Set time'
    return parsed.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  })()

  const selectDate = (nextDate) => {
    setDate(nextDate)
    setAppliedAt(value => replaceDate(value, nextDate))
  }

  // Stepper edits count as a manual touch so the wear-history prefill stops
  // overwriting the value (same guard as typing into the field).
  const adjustSprays = (delta) => {
    sprayTouched.current = true
    setSprays(current => String(Math.max(1, Math.min(30, (Number(current) || 1) + delta))))
  }

  const persistSprayOverride = async () => {
    if (parsedSprays == null) return
    setError('')
    try {
      await saveSprayOverride(parsedSprays)
    } catch (overrideError) {
      setError(overrideError?.message || 'Could not save this fragrance default.')
    }
  }

  const addLayer = (item) => {
    const label = `${item.house} - ${item.name}`
    setLayeredWith(current => current.includes(label) ? current : [...current, label])
    setLayerQuery('')
  }

  const save = async () => {
    if (layerSaveLock.current || logsLoading) return
    layerSaveLock.current = true
    setSaving(true)
    setError('')
    setErrorField(null)
    let failedField = null
    try {
      if (layerTargetId) {
        const target = sameDayWears.find(row => row.id === layerTargetId)
        if (!target || !fragrance.user_id || target.user_id !== fragrance.user_id) {
          setLayerTargetId('')
          throw new Error('This wear is no longer available. Choose the intended wear again.')
        }
        const additions = serializeLayerRows(selectedLayers)
        if (!additions.length) throw new Error('Choose a fragrance to add as a layer.')
        const layeredWith = [...new Set([...(target.layered_with || []), ...additions])]
        if (oilCount([fragrance, ...resolveLayerLabels(layeredWith, fragrances)]) > 3) {
          throw new Error('Layer sets can include up to 3 oils.')
        }
        const row = await updateLog(target.id, { layered_with: layeredWith }, { expectedLayers: target.layered_with ?? null })
        onSaved?.(row)
        onClose()
        return
      }
      if (sameDayWears.length > 0 && serializeLayerRows(selectedLayers).length > 0 && !duplicateAcknowledged) {
        throw new Error('Choose the saved wear for these layers, or explicitly choose a separate wear.')
      }
      if (sameDayWears.length > 0 && !duplicateAcknowledged) {
        failedField = 'duplicate_wear_confirmation_required'
        throw new Error('This bottle is already logged for this day. Choose a separate wear explicitly, or edit the saved wear.')
      }
      if (!appliedAt || Number.isNaN(new Date(appliedAt).getTime())) {
        failedField = 'applied_at'
        setAppliedAtEditing(true)
        throw new Error('Applied at: choose a valid date and time.')
      }
      const sprayCount = normalizeSprayCount(sprays)
      if (sprays !== '' && (sprayCount == null || Number(sprays) > 30 || Number(sprays) < 1)) {
        failedField = 'sprays'
        throw new Error('Sprays: enter a count between 1 and 30, or leave it blank.')
      }
      const row = await addLog(
        {
          ...buildWearLogEntry(fragrance, {
            date,
            occasion,
            note,
            sprays: sprayCount,
            timeBucket: selectedTimeBucket,
            eveningContext,
            layeredWith: serializeLayerRows(selectedLayers),
          }),
          wear_label: wearLabel,
          ...(projectionValue ? { performance_checkins: [projectionCheckin(projectionValue)] } : {}),
        },
        {
          applied_at: new Date(appliedAt).toISOString(),
          sprays: sprayCount,
        },
        {
          contextTags: selectedSignificantDate ? [selectedSignificantDate.canonicalTag] : [],
        },
      )
      emitWearFunnelEvent(telemetrySession, 'wear_committed', {
        fragranceId: fragrance.id,
        meta: { mode, wear_label: wearLabel },
      })
      onSaved?.(row)
      onClose()
    } catch (e) {
      // One-SOTD-per-day is DB-enforced — open the label picker so the wear can
      // be saved under another label instead of dead-ending.
      const activeSotd = /already have an SOTD/i.test(e?.message || '')
      emitWearFunnelEvent(telemetrySession, 'flow_blocked', {
        fragranceId: fragrance.id,
        blockedReason: failedField || (activeSotd ? 'active_sotd_occupying_slot' : 'wear_write_failed'),
        meta: { mode, wear_label: wearLabel },
      })
      if (activeSotd) setLabelPickerOpen(true)
      setErrorField(failedField)
      setError(e?.message || `Could not log ${mode === 'gym' ? 'gym scent' : 'SOTD'}.`)
    } finally {
      layerSaveLock.current = false
      setSaving(false)
    }
  }

  const retryOriginal=async()=>{setSaving(true);setError('');try{const row=await retryPendingCreate();onSaved?.(row);onClose()}catch(error){setError(error.message||'The original save is still unconfirmed. Retry the same save.')}finally{setSaving(false)}}

  const close = () => {
    abandonWearFunnelSession(telemetrySession, { reason: 'sheet_dismiss' })
    onClose()
  }

  return (
    <Modal updateEntry open={open} onClose={close} title={layerTargetId ? 'Add layers to saved wear' : mode === 'gym' ? 'Log gym scent' : mode === 'another' ? 'Log another wear' : 'Log SOTD'} maxWidth="max-w-lg" bodyClassName="!pb-0">
      {pendingCreate ? <section className="space-y-3 text-sm text-cream" aria-label="Pending wear save"><h3 className="font-semibold">Confirm your earlier wear save</h3><p>The original save may already exist. Retrying checks the same request and will not start another wear.</p><p>{pendingCreate.wear.fragrance_name || pendingCreate.display.name || 'Previously selected fragrance'} · {pendingCreate.wear.wear_label || 'SOTD'} · {pendingCreate.wear.worn_on}</p><p>Applied at {new Date(pendingCreate.application.applied_at).toLocaleString()} · {pendingCreate.application.sprays??'Unspecified'} sprays</p>{pendingCreate.wear.layered_with?.length>0&&<p>Layered with {pendingCreate.wear.layered_with.join(' + ')}</p>}{error&&<p role="alert" className="text-red-300">{error}</p>}<Button onClick={retryOriginal} disabled={saving}>{saving?'Checking original save…':'Retry original save'}</Button></section> : <div className="flex flex-col gap-3">
        <div ref={confirmationRef} data-wear-confirmation="modal">
          <p className="text-sm font-semibold leading-tight text-cream">{fragrance.name}</p>
          <p className="mt-0.5 text-xs text-cream/45">
            {fragrance.house}{fragrance.concentration ? ` · ${fragrance.concentration}` : ''}
          </p>
        </div>

        <CollectionLayerRows anchor={fragrance} collection={fragrances} selected={selectedLayers} onChange={setSelectedLayers} />

        {sameDayWears.length > 0 && (selectedLayers.length > 0 || layerTargetId) && (
          <fieldset className="rounded-xl border border-gold/30 p-3 text-sm text-cream">
            <legend className="px-1 text-gold">Where do these layers belong?</legend>
            {sameDayWears.map(row => (
              <label key={row.id} className="flex min-h-11 items-center gap-2">
                <input type="radio" name="layer-wear-target" checked={layerTargetId === row.id} disabled={saving}
                  onChange={() => { setLayerTargetId(row.id); setDuplicateAcknowledged(false); setError('') }} />
                Add layers to {row.wear_label || 'sotd'} at {new Date(row.applications?.[0]?.applied_at || row.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </label>
            ))}
            <label className="flex min-h-11 items-center gap-2">
              <input type="radio" name="layer-wear-target" checked={!layerTargetId && duplicateAcknowledged} disabled={saving}
                onChange={() => { setLayerTargetId(''); setDuplicateAcknowledged(true); setError('') }} />
              Start a separate wear with these layers
            </label>
            {layerTargetId && <p className="text-xs text-cream/60">Saved layers: {(sameDayWears.find(row => row.id === layerTargetId)?.layered_with || []).join(' + ') || 'None'}</p>}
            {layerTargetId && <p className="text-xs text-cream/60">Complete Log adds only the selected layers to this saved wear. Its existing layers, time, sprays and occasion stay as recorded.</p>}
          </fieldset>
        )}

        <button type="button" onClick={() => setLayeringOpen(value => !value)} className="text-left text-xs text-gold">{layeringOpen ? 'Hide pairing suggestions' : 'Browse pairing suggestions'}</button>
        {layeringOpen && <LayeringPicker anchor={fragrance} collection={fragrances} logs={logs} selected={selectedLayers} onChange={setSelectedLayers} />}

        <div className={layerTargetId ? 'hidden' : 'contents'}>
        {/* Applied at — one compact row (label left, time right); tap to edit. */}
        {appliedAtEditing ? (
          <input
            aria-label="Applied at"
            aria-invalid={errorField === 'applied_at'}
            type="datetime-local"
            autoFocus
            value={appliedAt}
            max={localDateTimeValue()}
            onChange={event => {
              const value = event.target.value
              setAppliedAt(value)
              if (value) {
                const parsed = new Date(value)
                setDate(value.split('T')[0])
                setSelectedTimeBucket(timeBucket(parsed))
              }
            }}
            onBlur={() => setAppliedAtEditing(false)}
            className="h-11 w-full rounded-lg border border-white/10 bg-charcoal-700 px-3 text-sm text-cream outline-none focus:border-gold/40"
          />
        ) : (
          <button
            type="button"
            onClick={() => setAppliedAtEditing(true)}
            className="flex h-11 w-full items-center justify-between rounded-lg border border-white/10 bg-charcoal-700 px-3 text-left outline-none focus:border-gold/40"
          >
            <span className="text-xs uppercase tracking-wide text-cream/50">Applied at</span>
            <span className="text-sm text-cream">{appliedAtLabel}</span>
          </button>
        )}

        {errorField === 'applied_at' && <p role="alert" className="text-xs text-red-400">{error}</p>}

        {/* Sprays — compact inline stepper on one row; prefill preserved. */}
        <div data-spray-defaults-loading={sprayDefaultsLoading ? 'true' : 'false'}>
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs uppercase tracking-wide text-cream/50">Sprays</span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                aria-label="Fewer sprays"
                onClick={() => adjustSprays(-1)}
                disabled={sprayDefaultsLoading}
                className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 bg-charcoal-700 text-xl leading-none text-cream outline-none hover:border-gold/40 focus:border-gold/40"
              >
                −
              </button>
              <input
                value={sprays}
                onChange={event => {
                  sprayTouched.current = true
                  setSprays(event.target.value.replace(/\D/g, '').slice(0, 2))
                }}
                inputMode="numeric"
                aria-label="Sprays"
                disabled={sprayDefaultsLoading}
                className="h-11 w-12 rounded-lg border border-white/10 bg-charcoal-700 px-1 text-center font-data text-cream outline-none focus:border-gold/40"
              />
              <button
                type="button"
                aria-label="More sprays"
                onClick={() => adjustSprays(1)}
                disabled={sprayDefaultsLoading}
                className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 bg-charcoal-700 text-xl leading-none text-cream outline-none hover:border-gold/40 focus:border-gold/40"
              >
                +
              </button>
            </div>
          </div>
          {errorField === 'sprays' && <p role="alert" className="text-xs text-red-400">{error}</p>}
          {sprayDefaultsLoading ? (
            <p className="mt-1 text-[11px] text-cream/35">Loading your spray default…</p>
          ) : sprayDefault !== '' && !showSprayOverridePrompt && (
            <p className="mt-1 text-[11px] text-sage">
              Prefilled from your {sprayOverride != null ? 'fragrance default' : 'wear default'}.
            </p>
          )}
          {showSprayOverridePrompt && (
            <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-gold/30 bg-gold/5 px-3 py-2">
              <p className="text-[11px] leading-4 text-cream/60">Save as default for this fragrance?</p>
              <button
                type="button"
                onClick={persistSprayOverride}
                disabled={sprayOverrideSaving}
                className="min-h-11 shrink-0 rounded-lg px-3 text-xs font-semibold text-gold disabled:opacity-40"
              >
                {sprayOverrideSaving ? 'Saving…' : 'Save default'}
              </button>
            </div>
          )}
        </div>

        {significantDates.length > 0 && (
          <div data-significant-date-context>
            <p className="mb-1.5 text-xs uppercase tracking-wide text-cream/50">Personal date context</p>
            <div className="flex flex-wrap gap-1.5">
              {significantDates.map(item => {
                const selected = item.id === selectedSignificantDateId
                return (
                  <button
                    key={item.id}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setSelectedSignificantDateId(selected ? null : item.id)}
                    className={`pill min-h-11 border px-3 py-1.5 text-xs ${selected ? 'border-gold bg-gold/10 text-gold' : 'border-white/15 text-cream/55'}`}
                  >
                    {item.label}
                  </button>
                )
              })}
            </div>
            <p className="mt-1 text-[11px] text-cream/35">Optional — never added automatically.</p>
          </div>
        )}

        <div className="rounded-xl border border-white/8 bg-charcoal-900/25 p-2.5">
          <ProjectionCheckin value={projectionValue} onChange={setProjectionValue} compact />
        </div>

        {labelPickerOpen ? (
          <div>
            <p className="mb-1.5 text-xs uppercase tracking-wide text-cream/50">Wear label</p>
            <div className="flex flex-wrap gap-1.5">
              {WEAR_LABEL_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setWearLabel(option.value)}
                  className={`pill border px-3 py-1.5 text-xs ${wearLabel === option.value ? 'border-gold bg-gold/10 text-gold' : 'border-white/15 text-cream/50'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-cream/35">One SOTD per day — every other label can repeat.</p>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setLabelPickerOpen(true)}
            className="self-start text-xs text-cream/40 underline underline-offset-2 hover:text-cream/70"
          >
            Logging as {WEAR_LABEL_OPTIONS.find(option => option.value === wearLabel)?.label || wearLabel} · change
          </button>
        )}

        {expanded && (
          <div className="flex flex-col gap-4 border-t border-white/8 pt-4">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => selectDate(todayISO())}
                className={`pill flex-1 border px-3 py-1.5 text-xs ${date === todayISO() ? 'border-gold bg-gold/10 text-gold' : 'border-white/15 text-cream/55'}`}
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => selectDate(yesterdayISO())}
                className={`pill flex-1 border px-3 py-1.5 text-xs ${date === yesterdayISO() ? 'border-gold bg-gold/10 text-gold' : 'border-white/15 text-cream/55'}`}
              >
                Yesterday
              </button>
            </div>

            <div>
              <p className="mb-1.5 text-xs uppercase tracking-wide text-cream/50">Time of day</p>
              <div className="grid grid-cols-4 gap-1.5">
                {TIME_BUCKETS.map(bucket => (
                  <button
                    key={bucket}
                    type="button"
                    onClick={() => setSelectedTimeBucket(bucket)}
                    className={`rounded-lg border px-1 py-2 text-[11px] capitalize ${selectedTimeBucket === bucket ? 'border-gold bg-gold/10 text-gold' : 'border-white/10 text-cream/50'}`}
                  >
                    {bucket}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-1.5 text-xs uppercase tracking-wide text-cream/50">Evening context</p>
              <div className="flex flex-wrap gap-1.5">
                {EVENING_CONTEXTS.map(context => (
                  <ContextPill as="button" tag={context}
                    key={context}
                    type="button"
                    onClick={() => setEveningContext(eveningContext === context ? '' : context)}
                    className={`pill border px-3 py-1.5 text-xs capitalize ${eveningContext === context ? 'border-gold bg-gold/10 text-gold' : 'border-white/15 text-cream/50'}`}
                  >
                    {context}
                  </ContextPill>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-1.5 text-xs uppercase tracking-wide text-cream/50">Occasion (optional)</p>
              <div className="flex flex-wrap gap-1.5">
                {QUICK_WEAR_OCCASIONS.map(item => (
                  <ContextPill as="button" tag={item}
                    key={item}
                    type="button"
                    onClick={() => setOccasion(occasion === item ? '' : item)}
                    className={`pill border px-3 py-1 text-xs ${occasion === item ? 'border-gold bg-gold/10 text-gold' : 'border-white/15 text-cream/50'}`}
                  >
                    {item}
                  </ContextPill>
                ))}
              </div>
            </div>

            <div>
              <textarea
                value={note}
                onChange={event => setNote(event.target.value)}
                rows={2}
                placeholder="Optional note"
                className="w-full scroll-mb-28 resize-none rounded-lg border border-white/10 bg-charcoal-700 px-3 py-2 text-sm text-cream outline-none placeholder:text-cream/30 focus:border-gold/40"
              />
            </div>
          </div>
        )}

        </div>

        {sameDayWears.length > 0 && !layerTargetId && selectedLayers.length === 0 && (
          <div className={`rounded-xl border p-3 ${duplicateAcknowledged ? 'border-amber-400/40 bg-amber-400/10' : 'border-amber-400/20 bg-amber-400/5'}`}>
            <p className="text-xs font-semibold text-amber-200">
              {duplicateAcknowledged ? 'Log this a second time?' : 'Already logged'}
            </p>
            <p className="mt-1 text-[11px] leading-relaxed text-cream/70">
              You already logged {fragrance.name} {date === todayISO() ? 'today' : `on ${date}`}
              {sameDayWears.length > 1 ? ` (${sameDayWears.length} times)` : ''}:
              {' '}
              {sameDayWears
                .map(log => `${(log.wear_label || 'sotd')} at ${new Date(log.applications?.[0]?.applied_at || log.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`)
                .join(', ')}.
            </p>
            <label className="mt-2 flex min-h-11 items-center gap-2 text-xs text-cream">
              <input type="checkbox" checked={duplicateAcknowledged} disabled={saving}
                onChange={event => setDuplicateAcknowledged(event.target.checked)} />
              This is a separate wear, not a correction or a retry
            </label>
            <p className="text-xs text-cream/60">To correct the saved fragrance, open that wear and choose Edit. For a top-up, choose Reapply.</p>
          </div>
        )}

        {/* Pinned to the bottom of the sheet above the home indicator so the
            log action stays visible no matter how tall the content gets. */}
        <div
          className="sticky bottom-0 -mx-4 flex flex-col gap-2 border-t border-white/10 bg-charcoal-800 px-4 pt-3 sm:-mx-6 sm:px-6"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
        >
          {(error || pendingCreateError || sprayOverrideError || significantDatesError) && (
            <p ref={saveErrorRef} role="alert" data-save-error className="text-xs text-red-400">{error || pendingCreateError || sprayOverrideError || significantDatesError}</p>
          )}
          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => setExpanded(value => !value)} className="flex-1">
              {expanded ? 'Hide details' : 'Add details'}
            </Button>
            <Button onClick={() => save()} disabled={saving || logsLoading || Boolean(pendingCreateError)} className="flex-1">
              {saving ? <><Spinner size="sm" /> Saving...</> : 'Complete Log'}
            </Button>
          </div>
        </div>
      </div>}
    </Modal>
  )
}
